import * as t from 'io-ts';

import {colorForCompanyId} from '../../helpers/colorHelpers';
import {makeApiType} from '../apiTypes';
import {parseAuthorityPermissions, RAuthorityPermissions} from '../authorityModel';
import {parseBillingLicenses, RBillingLicenses} from '../billing/billingModel';
import {parseCompanyPreferences, RCompanyPreferences} from '../companyPreferencesModel';
import {PaletteColorsEnum} from '../paletteModel';
import {makeSafeArrayType} from '../types/arrayTypes';
import {makeEnumRuntimeType, makeEnumTypeGuard} from '../types/enumTypes';
import {makeOptional} from '../types/optionalType';

/*
 * Runtime type.
 */

export const defaultCompanyName = '[Unknown Company Name]';

export const pastDueInvoiceString = 'past_due_invoices';

export const recentChargeFailedString = 'recent_charge_failed';

export enum CompanyTrialPlansEnum {
  TRIAL = 'trial',
  FREE = 'free',
  MANAGED_TRIAL_PRIME = 'managed-trial-prime',
  MANAGED_TRIAL_ENTERPRISE = 'managed-trial-enterprise',
  MANAGED_TRIAL_GROWTH = 'managed-trial-growth',
  MANAGED_TRIAL_SCALE = 'managed-trial-scale',
}
export const isCompanyTrialPlan = makeEnumTypeGuard(CompanyTrialPlansEnum);
export const DEVELOPERS_PLAN = 'developers';

export enum CompanyFeaturesEnum {
  ACCOUNT_RULES = 'account_rules',
  ACCESS_MANAGEMENT_BASIC = 'access_management_basic',
  ACCESS_MANAGEMENT = 'access_management',
  API = 'api',
  ANALYTICS = 'analytics',
  ACCOUNT_ANALYTICS = 'account_analytics',
  ADVANCED_ASSIGNING_RULES = 'advanced_assigning_rules',
  ANALYTICS_BASIC = 'analytics_basic',
  ADVANCED_ANALYTICS = 'advanced_analytics',
  ANALYTICS_LIVE_DASHBOARD = 'analytics_live_dashboard',
  ANALYTICS_CUSTOM_REPORT = 'analytics_custom_report',
  ANALYTICS_RETENTION_217 = 'analytics_retention:217', // 7 months.
  ANALYTICS_RETENTION_775 = 'analytics_retention:775', // 25 months.
  ANALYTICS_RETENTION_UNLIMITED = 'analytics_retention:+', // Unlimited.
  CONVERSATION_STAGES = 'conversation_stages',
  RULES = 'rules',
  ROLES = 'roles',
  SEQUENCES = 'sequences',
  FRONT_CHAT = 'front-chat',
  CHANNELS = 'channels',
  CHAT = 'chat',
  CHAT_LICENSES = 'chat-licenses',
  CHAT_CUSTOM_BRANDING = 'chat:custom-branding',
  SUPPORT_CHAT = 'support-chat',
  TEAMS = 'teams',
  TEAMS_UNLIMITED = 'teams:+',
  TEAMMATES = 'teammates',
  ADVANCED_RULES = 'advanced_rules',
  GLOBAL_RULES = 'global_rules',
  MACROS = 'macros',
  GLOBAL_MACROS = 'global_macros',
  SCIM = 'scim',
  SSO = 'sso',
  INTEGRATIONS = 'int:all',
  SLA_RULES = 'sla_rules',
  SHIFTS = 'shifts',
  SURVEYS = 'surveys',
  CUSTOM_VIEWS = 'custom_views',
  CUSTOM_VIEWS_UNLIMITED = 'custom_views:+',
  CUSTOM_VIEWS_TRIAL = 'custom_views:trial',
  REQUIRED_TAGGING_RULES = 'required_tagging_rules',
  SMART_RULES = 'smart_rules',
  TEAMMATE_ACTIVITY_EXPORTS = 'teammate_activity_exports',
  TEAMMATE_TEMPLATES = 'teammate_templates',
  CRM_SYNC = 'crm_sync',
  CHAT_ADVANCED = 'chat:advanced',
  CHAT_BOTS = 'chat:bots',
  LENSES_TEAM = 'saved_views:team',
  CHATBOT_EXTENDED_FEATURES = 'chatbot_extended_features',
  AI_COMPOSE = 'ai_compose',
  AI_COMPOSE_ASSISTANT = 'ai_compose_assistant',
  AI_SUMMARIZE = 'ai_summarize',
  AI_TAGGING = 'ai_tagging',
  AI_SYNOPSIS = 'ai_synopsis',
  AI_TRANSLATE = 'ai_translate',
  AI_INTERNAL_ANSWERS = 'ai_internal_answers',
  CUSTOMER_PORTAL = 'channel:portal',
  KNOWLEDGE_BASE = 'knowledge-base',
  KNOWLEDGE_BASE_MULTIPLE = 'knowledge-base-multiple',
  KNOWLEDGE_BASE_PUBLISHED_ARTICLES = 'knowledge-base-published-articles',
  KNOWLEDGE_BASE_VERSION_HISTORY = 'knowledge-base:version-history',
  KNOWLEDGE_BASE_PUBLISHED_LOCALES = 'knowledge-base-published-locales',
  KNOWLEDGE_BASE_CUSTOM_BRANDING = 'knowledge-base:custom-branding',
  KNOWLEDGE_BASE_CUSTOM_DOMAINS = 'knowledge-base:custom-domains',
  KNOWLEDGE_BASE_FRONT_CHAT = 'knowledge-base:front-chat',
  KNOWLEDGE_BASE_DEPTH = 'knowledge-base-depth',
  KNOWLEDGE_BASE_ANALYTICS = 'knowledge-base:analytics',
  KNOWLEDGE_BASE_AI_EDITING = 'knowledge-base:ai-editing',
  KNOWLEDGE_BASE_RESTRICTED = 'knowledge-base:restricted',
  KNOWLEDGE_BASE_CUSTOMER_PORTAL = 'knowledge-base:customer-portal',
  KNOWLEDGE_BASE_ARTICLE_REVIEWS = 'knowledge-base:article-reviews',
  APPLICATION_REQUESTS = 'application-requests',
  APPLICATION_OBJECTS = 'application-objects',
  APPLICATION_TRIGGERS = 'application-triggers',
  LINKED_CONVERSATIONS = 'linked_conversations',
  APPLICATION_OAUTH2_PASSWORD_GRANT_TYPE = 'application-oauth-2-password-grant-type',
  TELEPHONY = 'telephony_features',
  WORKSPACE_PERMISSIONS = 'workspace_permissions',

  // premium add-ons feature
  WHATSAPP = 'channel:whatsapp',
  DIALPAD_SMS = 'channel:dialpad_sms',
  TELEGRAM = 'channel:telegram',
  YALO = 'channel:yalo',
  AI_ANSWERS = 'ai_answers',
  AI_AUTOPILOT = 'ai_autopilot',
  AI_SMART_QA = 'ai_smart_qa',
  AI_SMART_CSAT = 'ai_smart_csat',
  AI_COPILOT = 'ai_copilot',
  WORKFORCE_MANAGEMENT = 'workforce_management',
  API_RATE_100 = 'api_rate_100',

  // plan
  TIER3_PLAN = 'tier3_plan',

  /** Ticketing */
  TICKET_IDS = 'ticket-ids',
  TICKET_STATUSES = 'ticket-statuses',

  /** Optional Branding */
  INVITE_OPTIONAL_BRANDING = 'invite-optional-branding',

  /** Bulk Reply */
  BULK_REPLY = 'bulk-reply',

  /** Channel Types */
  CHANNEL_TYPES = 'channel_types',

  CHANNEL_TYPES_1 = 'channel_types:1',
  CHANNEL_TYPES_UNLIMITED = 'channel_types:+',
}
export const isCompanyFeaturesEnum = makeEnumTypeGuard(CompanyFeaturesEnum);

export const RCompanyFeatures = makeEnumRuntimeType(CompanyFeaturesEnum);

export const RCompany = t.readonly(
  t.type({
    id: t.Integer,
    url: t.string,
    slug: makeOptional(t.string),
    features: makeSafeArrayType(t.string),
    availableTrialFeatures: makeSafeArrayType(t.string),
    plan: makeOptional(t.string),
    name: t.string,
    region: makeOptional(t.string),
    shard: makeOptional(t.string),
    cell: makeOptional(t.string),
    futureCell: makeOptional(t.string),
    subdomain: t.string,
    preferences: RCompanyPreferences,
    color: makeEnumRuntimeType(PaletteColorsEnum),
    isInTrial: t.boolean,
    isInManagedTrial: t.boolean,
    trialEndDate: makeOptional(t.Integer),
    hasStripeId: t.boolean,
    status: makeOptional(t.string),
    stripeError: makeOptional(t.string),
    discountEndDate: makeOptional(t.Integer),
    createdAt: t.Integer,
    updatedAt: t.Integer,
    isBilledThroughOtherCompany: t.boolean,
    licenses: t.union([RBillingLicenses, t.null]),
    numInboxes: makeOptional(t.Integer),
    authorityPermissions: RAuthorityPermissions,
  }),
  'company',
);

/*
 * Type.
 */

export interface CompanyModel extends t.TypeOf<typeof RCompany> {}

/*
 * IO methods.
 */

export function parseCompany(src: any): CompanyModel {
  return {
    id: src.id,
    slug: src.slug,
    url: src.url,
    name: src.name,
    subdomain: src.subdomain,
    plan: src.plan || undefined,
    region: src.region || undefined,
    shard: src.shard || undefined,
    cell: src.cell || undefined,
    futureCell: src.future_cell || undefined,
    features: src.features || [],
    availableTrialFeatures: src.available_trial_features || [],
    preferences: parseCompanyPreferences(src.preferences),
    color: colorForCompanyId(src.id),
    isInTrial: src.is_trial || false,
    isInManagedTrial: src.is_managed_trial || false,
    trialEndDate: src.trial_end || undefined,
    hasStripeId: Boolean(src.has_stripe_id),
    status: src.status || undefined,
    stripeError: src.stripe_error || undefined,
    discountEndDate: src.discount_end || undefined,
    createdAt: src.created_at,
    updatedAt: src.updated_at,
    isBilledThroughOtherCompany: Boolean(src.billed_through_other_company),
    licenses: parseBillingLicenses(src.licenses),
    numInboxes: src.num_inboxes,
    authorityPermissions: parseAuthorityPermissions(src.authority_permissions, undefined),
  };
}

/*
 * API type.
 */

export const ApiCompany = makeApiType('company', RCompany, parseCompany);
